import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import PasswordInput from "../components/PasswordInput";
import PasswordStrengthIndicator, { isPasswordStrong } from "../components/PasswordStrengthIndicator";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Target, ShieldCheck, ArrowLeft } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function ResetPassword() {
  useDocumentTitle("Reset password");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("This reset link is missing its token. Request a new one."); return; }
    if (!isPasswordStrong(pw)) {
      toast.error("Password must be 8+ chars with uppercase, lowercase, and a special character.");
      return;
    }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="h-8 w-8 rounded-md bg-[#002FA7] text-white grid place-items-center">
            <Target className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display font-black tracking-tighter text-zinc-950">
            Qa<span className="text-[#002FA7]">Testology</span>
          </span>
        </Link>

        <Card className="p-6 sm:p-8">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1 flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> reset password
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter text-zinc-950">
              Choose a new password
            </h1>
            <p className="text-sm text-zinc-600 mt-2">
              Use at least 8 characters. Include an uppercase letter, a lowercase letter, and a special character.
            </p>
          </div>
          {!token ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" data-testid="reset-no-token">
              This reset link is missing its token. Please request a fresh one from the{" "}
              <Link to="/forgot-password" className="underline">forgot password page</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <PasswordInput
                  id="pw"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-new-password"
                  showTestId="reset-new-password-toggle"
                  className="mt-1.5"
                  autoFocus
                />
                <PasswordStrengthIndicator password={pw} />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm new password</Label>
                <PasswordInput
                  id="pw2"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-confirm-password"
                  showTestId="reset-confirm-password-toggle"
                  className="mt-1.5"
                />
                {pw2 && pw !== pw2 && (
                  <p className="text-xs text-red-600 mt-1">Passwords don&apos;t match yet.</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white h-11"
                data-testid="reset-submit-btn"
              >
                {saving ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
          <div className="mt-6 pt-5 border-t border-zinc-100 text-sm text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-zinc-600 hover:text-[#002FA7]" data-testid="back-to-login-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
